import type { FlowData, ConnectionState } from '@/types';
import { CONFIG } from '@/config';
import { store } from '@/state';
import { addEventToFeed } from '@/ui/events';
import { updateConnectionStatus } from '@/ui/stats';

/** Reconnect delay in milliseconds */
const RECONNECT_DELAY_MS = 5000;

/**
 * Validate incoming flow data
 * @param data - Parsed JSON data
 * @returns True if data is valid FlowData
 */
export function isValidFlowData(data: unknown): data is FlowData {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const flow = data as Record<string, unknown>;

  return (
    typeof flow.srcLat === 'number' &&
    typeof flow.srcLng === 'number' &&
    typeof flow.dstLat === 'number' &&
    typeof flow.dstLng === 'number' &&
    typeof flow.mbps === 'number' &&
    typeof flow.threat === 'number'
  );
}

/**
 * Handle incoming WebSocket message
 * @param event - Message event
 */
function handleMessage(event: MessageEvent): void {
  try {
    const flow = JSON.parse(event.data as string);

    if (isValidFlowData(flow)) {
      store.queueFlow(flow);

      // Add high-threat flows to event feed
      if (flow.threat >= 4) {
        addEventToFeed({
          severity: flow.threat >= 5 ? 'critical' : 'error',
          source: `Threat L${flow.threat}`,
          message: 'High-threat flow detected',
          meta: `${flow.mbps.toFixed(1)} Mbps`,
        });
      }
    }
  } catch (error) {
    console.error('Error parsing flow data:', error);
  }
}

/**
 * Handle WebSocket connection open
 */
function handleOpen(): void {
  console.log('WebSocket connected');
  updateConnectionStatus('Connected', 'connected');
  addEventToFeed({
    severity: 'info',
    source: 'System',
    message: 'WebSocket connection established',
    meta: CONFIG.websocketUrl,
  });
}

/**
 * Handle WebSocket error
 * @param error - Error event
 */
function handleError(error: Event): void {
  console.error('WebSocket error:', error);
  updateConnectionStatus('Error', 'disconnected');
  addEventToFeed({
    severity: 'error',
    source: 'System',
    message: 'WebSocket connection error',
  });
}

/**
 * Handle WebSocket close
 */
function handleClose(): void {
  console.log('WebSocket disconnected');
  updateConnectionStatus('Offline', 'disconnected');
  addEventToFeed({
    severity: 'warn',
    source: 'System',
    message: 'WebSocket disconnected, reconnecting...',
  });

  // Schedule reconnection
  setTimeout(connectWebSocket, RECONNECT_DELAY_MS);
}

/**
 * Connect to the WebSocket server
 */
export function connectWebSocket(): void {
  console.log('Connecting to WebSocket:', CONFIG.websocketUrl);

  const websocket = new WebSocket(CONFIG.websocketUrl);

  websocket.onopen = handleOpen;
  websocket.onmessage = handleMessage;
  websocket.onerror = handleError;
  websocket.onclose = handleClose;

  store.set('websocket', websocket);
}

/**
 * Disconnect from the WebSocket server
 */
export function disconnectWebSocket(): void {
  const websocket = store.get('websocket');
  if (websocket) {
    websocket.close();
    store.set('websocket', null);
  }
}

/**
 * Check if WebSocket is connected
 * @returns True if connected
 */
export function isWebSocketConnected(): boolean {
  const websocket = store.get('websocket');
  return websocket !== null && websocket.readyState === WebSocket.OPEN;
}

/**
 * Get current connection state
 * @returns Connection state
 */
export function getConnectionState(): ConnectionState {
  const websocket = store.get('websocket');

  if (!websocket) {
    return 'disconnected';
  }

  switch (websocket.readyState) {
    case WebSocket.CONNECTING:
      return 'connecting';
    case WebSocket.OPEN:
      return 'connected';
    default:
      return 'disconnected';
  }
}
